
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { match_redirect_token } from "@/store/apis/auth";
import {
  SET_USER,
  SET_COMPANY,
  USER_STATE
} from "@/store/modules/auth/constants";
import {
  INIT_COMMON_DATA,
  GET_ROLE_BY_NAME
} from "@/store/modules/common/constants";
import { INIT_CANDIDATE } from "@/store/modules/candidates/constants";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import { UserState } from "@/store/modules/auth/interfaces";
import router from "@/router";

export default Vue.extend({
  name: "CandidateRedirectRoute",
  data() {
    return {
      loading: true,
      error: null as string | null,
      success: false
    };
  },
  computed: {
    ...mapGetters("common", {
      get_role_by_name: GET_ROLE_BY_NAME
    }),
    ...mapGetters("auth", {
      get_user_state: USER_STATE
    })
  },
  async mounted() {
    // Support both 'token' and 'redirect_token' query parameters
    const redirect_token =
      (this.$route.query.redirect_token as string) ||
      (this.$route.query.token as string);

    if (!redirect_token) {
      this.loading = false;
      this.error = "Token parameter is missing from URL";
      return;
    }

    try {
      this.loading = true;

      const ssoResponse = await match_redirect_token(redirect_token);

      if (!ssoResponse || !ssoResponse.data) {
        throw new Error("Invalid response from token validation");
      }

      const user = ssoResponse.data.data.user;
      const token = ssoResponse.data.data.token;
      const company = ssoResponse.data.data.user.companyDetail;

      // Store user data and token
      this.$store.commit(`auth/${SET_USER}`, user);
      this.$store.commit(`auth/${SET_COMPANY}`, company);
      localStorage.setItem("token", token);
      if (user.refresh_token) {
        localStorage.setItem("refresh_token", user.refresh_token);
      }

      // Initialize common data (roles, industries, etc.)
      const init_common = await this.init_common_data();
      if (!init_common) {
        this.root_error(this.$t("errors.internal").toString());
        this.loading = false;
        return;
      }

      // Check if user is a candidate (individual role)
      const candidate_role = this.get_role_by_name("individual");
      if (candidate_role && candidate_role.id === user.role_id) {
        // Initialize candidate data
        const candidate_initialize = await this.init_candidate();
        if (!candidate_initialize) {
          this.set_user_state(UserState.ROOT_ERROR);
          this.loading = false;
          await router.push("/error");
          return;
        }
        // Set user state to candidate
        if (this.get_user_state !== UserState.CANDIDATE_PROFILE_PENDING) {
          this.set_user_state(UserState.CANDIDATE);
        }
        // After successful candidate initialization, redirect to annotation assessment
        this.success = true;
        await router.push("/candidate/assessments/annotation");
      } else {
        // If user is not a candidate, show error
        this.root_error("SSO ticket is only valid for candidate users.");
        this.loading = false;
        return;
      }
    } catch (err: any) {
      this.root_error(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid or expired token. Please try again."
      );
      this.loading = false;
      this.error =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while processing the token";
    }
  },
  methods: {
    ...mapMutations({
      root_error: ROOT_ERROR
    }),
    ...mapMutations("auth", {
      set_user_state: USER_STATE
    }),
    ...mapActions("common", {
      init_common_data: INIT_COMMON_DATA
    }),
    ...mapActions("candidate", {
      init_candidate: INIT_CANDIDATE
    })
  }
});
